import { action, computed, makeObservable, observable } from "mobx";
import { ClientDataType, ClientModel, DefaultClient, LegalFormClientData } from "../helpers/client";
import Validator, { ValidatedValue } from "../helpers/validator";
import { ErrorMessages } from "../helpers/errorMessage";
import { DefaultRequestStatus, RequestStatus } from "../services/services/requestStatus";

export interface ClientStore {
  uwRulesRequestStatus: RequestStatus;
  clients: ClientModel[];
  setClients: (clients: ClientModel[]) => void;
  addInsured: () => void;
  removeInsured: (clientId: number) => void;
  insurer: ClientModel;
  insurerName: string;
  insureds: ClientModel[];
  resetStore: () => void;
  insuredInSecondOpinion: ClientModel | null;
  setInsuredInSecondOpinion: (pesel: string) => void;

  setEmailForClientEnds: (email: string) => void;
  emailForClientEnds: ValidatedValue<string>;

  generateClientEndsLinkRequestStatus: RequestStatus;
  generateRemotePaymentLinkRequestStatus: RequestStatus;
}

export class DefaultClientStore implements ClientStore {
  constructor() {
    makeObservable(this);
  }

  @computed
  public get clients() {
    return this._clients;
  }

  @observable
  private _clients: ClientModel[] = [new DefaultClient()];

  @action
  public setClients(clients: ClientModel[]) {
    this._clients = clients;
  }

  @action
  public addInsured() {
    this._clients.push(new DefaultClient({ insurer: false, insured: true }));
  }

  @action
  public removeInsured(clientId: number) {
    const index = this._clients.findIndex(client => client.id === clientId);
    this._clients.splice(index, 1);
  }

  @computed
  public get insurer() {
    return this._clients.filter(client => client.insurer)[0];
  }

  @computed
  public get insureds() {
    return this._clients.filter(client => client.insured);
  }

  @action
  public resetStore() {
    this._clients = [new DefaultClient()];
    this._emailForClientEnds.setValue("");
    this._generateClientEndsLinkRequestStatus = new DefaultRequestStatus();
  }

  @computed
  public get insurerName() {
    const legalForm = this.insurer.legalForm!;
    const clientDataType = LegalFormClientData[legalForm.value!];
    if (clientDataType === ClientDataType.PERSON) {
      return this.insurer.firstName.value! + " " + this.insurer.lastName.value!;
    }
    if (clientDataType === ClientDataType.COMPANY) {
      return this.insurer.companyName!.value!;
    }
    if (clientDataType === ClientDataType.PERSON_AND_COMPANY) {
      return this.insurer.firstName!.value + " " + this.insurer.lastName!.value + " " + this.insurer.companyName!.value!;
    }
    return "";
  }

  @computed
  public get insuredInSecondOpinion() {
    const insuredInSecondOpinion = this._clients.filter(client => client.isInsuredInSecondOpinion);

    return insuredInSecondOpinion ? insuredInSecondOpinion[0] : null;
  }

  @action
  public setInsuredInSecondOpinion(pesel: string) {
    if (this.insuredInSecondOpinion) {
      this.insuredInSecondOpinion.isInsuredInSecondOpinion = false;
    }

    const insuredInSecondOpinion = this._clients.filter(client => client.pesel.value === pesel);

    if (insuredInSecondOpinion.length) {
      insuredInSecondOpinion[0].isInsuredInSecondOpinion = true;
    }
  }

  @computed
  public get emailForClientEnds() {
    return this._emailForClientEnds;
  }

  @observable
  private _emailForClientEnds = new ValidatedValue("", [
    { name: "clientEmailIsNotNull", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required },
    { name: "clientEmailIsValid", fn: Validator.email, errorMessage: ErrorMessages.client.email }
  ]);

  @action
  public setEmailForClientEnds(email: string) {
    this._emailForClientEnds.setValue(email);
  }

  @observable
  private _generateClientEndsLinkRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get generateClientEndsLinkRequestStatus(): RequestStatus {
    return this._generateClientEndsLinkRequestStatus;
  }
  @observable
  private _generateRemotePaymentLinkRequestStatus: RequestStatus = new DefaultRequestStatus();
  @computed
  public get generateRemotePaymentLinkRequestStatus(): RequestStatus {
    return this._generateRemotePaymentLinkRequestStatus;
  }
  @observable
  private _uwRulesRequestStatus = new DefaultRequestStatus();
  @computed
  public get uwRulesRequestStatus(): RequestStatus {
    return this._uwRulesRequestStatus;
  }
}
